import { useEffect, useRef, useState } from 'react';
import { fetchProductById } from '../utils/apiHelpers';
import { Link } from 'react-router-dom';
import { Heart, Eye, ArrowLeftRight, X } from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import { useWishlistStore } from '../store/useWishlistStore';
import { useToast } from '../context/ToastContext';
import { getImageUrl } from '../utils/imageHelper';
import {
  isPromotionActive,
  getPromotionName,
  calculateDiscountedPrice,
} from '../utils/promotion';
import type { Product } from '../utils/apiHelpers';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const addItem = useCartStore((s) => s.addItem);
  const { addItem: addWishlist, removeItem: removeWishlist, isWishlisted } =
    useWishlistStore();
  const { showToast } = useToast();
  const wishlisted = isWishlisted(product.id);

  const images = product.images?.length
    ? product.images.map((i) => i.url).filter(Boolean) as string[]
    : product.product_image
      ? [product.product_image]
      : [];
  const [localImages, setLocalImages] = useState<string[] | null>(null);
  const colorVariants = (product.variants ?? []).filter((variant) => variant.color?.trim());
  const activeVariantIndex = colorVariants.length > 0
    ? Math.min(selectedVariantIndex, colorVariants.length - 1)
    : 0;
  const selectedVariant = colorVariants[activeVariantIndex];
  const imagesSource = localImages ?? images;
  const fallbackImage = imagesSource[0] || product.product_image;
  const activeImage = selectedVariant?.image || imagesSource[selectedImageIndex] || fallbackImage;
  const displayedImageUrl = getImageUrl(activeImage) || '';
  const hasPromo = isPromotionActive(product);
  const thumbnailImages = imagesSource;
  const brandName = product.vendor_name || (product as any).extra?.brand_name || (product as any).extra?.brand || (product as any).extra?.vendor_details?.store_name || 'Brand';
  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
  };
  const price = parseFloat(product.price);
  const displayPrice = hasPromo && product.promotion
    ? calculateDiscountedPrice(price, product.promotion)
    : price;
  const basePrice = product.base_price
    ? parseFloat(product.base_price)
    : price;
  const discountPct =
    basePrice > price
      ? Math.round(((basePrice - displayPrice) / basePrice) * 100)
      : 0;

  useEffect(() => {
    if (selectedImageIndex >= (imagesSource?.length ?? 0)) {
      setSelectedImageIndex(0);
    }
  }, [imagesSource?.length, selectedImageIndex]);

  // CHANGE: only fetch the full product (for additional images) once this
  // card is actually near the viewport, instead of firing on mount for
  // every card in the grid at once. That upfront N-requests burst was the
  // main cause of slow initial load. Behavior/result is otherwise identical.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const needsFetch = images.length <= 1 || !images.length;
    if (!needsFetch || !product?.id) return;
    const el = cardRef.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      // No IO support: fall back to old immediate behavior
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' } // start loading a bit before it's on screen
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [product?.id, images.length]);

  useEffect(() => {
    let cancelled = false;
    if (!isNearViewport || !product?.id) return;
    if (images.length > 1) return;

    fetchProductById(product.id)
      .then((full) => {
        if (cancelled) return;
        console.info('Fetched full product:', full);
        const imgs: string[] = [];
        if (Array.isArray(full.images) && full.images.length) {
          full.images.forEach((it) => {
            const url = (it as any).url || (it as any).image;
            if (url) imgs.push(url);
          });
        }
        // fallback to product_image and extra.additional_images
        if (!imgs.length && (full as any).product_image) imgs.push((full as any).product_image);
        if (Array.isArray((full as any).extra?.additional_images) && (full as any).extra.additional_images.length) {
          ((full as any).extra.additional_images as any[]).forEach((ai) => {
            const url = ai?.image || ai?.url;
            if (url && !imgs.includes(url)) imgs.push(url);
          });
        }
        if (imgs.length) setLocalImages(imgs);
      })
      .catch(() => {
        /* ignore */
      });

    return () => {
      cancelled = true;
    };
  }, [isNearViewport, product?.id, images.length]);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      id: product.id,
      name: product.name,
      price: displayPrice,
      image: displayedImageUrl,
      quantity: 1,
    });
    showToast('Product added to cart!', 'success');
  };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (wishlisted) {
      removeWishlist(product.id);
      showToast('Removed from wishlist', 'info');
    } else {
      addWishlist({
        id: product.id,
        name: product.name,
        price: product.price,
        image: displayedImageUrl,
        addedAt: Date.now(),
      });
      showToast('Added to wishlist', 'success');
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewOpen(true);
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    showToast('Compare feature coming soon', 'info');
  };

  return (
    <>
      <div ref={cardRef} className="group rounded-lg overflow-hidden bg-[var(--background)]">
        <div
          className="relative aspect-square bg-gray-100 dark:bg-gray-800 overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Link to={`/product/${product.id}`} className="block h-full">
            <div
              className="relative h-full w-full"
              style={{ perspective: '1000px' }}
            >
              <div
                className="relative h-full w-full transition-transform duration-500"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: isHovered ? 'rotateY(180deg)' : 'none',
                }}
              >
                <img
                  src={displayedImageUrl}
                  alt={product.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-fill object-center bg-white"
                  style={{ backfaceVisibility: 'hidden' }}
                />
                <div
                  className="absolute inset-0 flex flex-col justify-start bg-white px-4 py-4 text-left overflow-y-auto"
                  style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                >
                  <p className="text-sm font-semibold text-[var(--foreground)] line-clamp-2">
                    {product.name}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span className="rounded-full bg-orange-50 px-2 py-1 text-orange-600">
                      {product.category_name ?? 'Category'}
                    </span>
                    <span className="font-medium text-gray-700">Rs {displayPrice.toLocaleString()}</span>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-gray-700">
                    <p><span className="font-medium">Stock:</span> {product.stock != null ? product.stock : 'N/A'}</p>
                    <p><span className="font-medium">Brand:</span> {brandName}</p>
                    {selectedVariant?.color && (
                      <p><span className="font-medium">Color:</span> {selectedVariant.color}</p>
                    )}
                  </div>
                  {colorVariants.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {colorVariants.map((variant, index) => {
                        const swatchColor = variant.color?.trim();
                        const swatchStyle = swatchColor && /^(#|rgb|hsl)/i.test(swatchColor)
                          ? { backgroundColor: swatchColor }
                          : { backgroundColor: swatchColor || '#d1d5db' };
                        return (
                          <span
                            key={variant.id}
                            className={`h-4 w-4 rounded-full border ${activeVariantIndex === index ? 'border-[#ff9100]' : 'border-gray-300'}`}
                            style={swatchStyle}
                            title={variant.color}
                          />
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-500 line-clamp-4">
                    {product.description || 'Tap for more product details.'}
                  </p>
                </div>
              </div>
            </div>
          </Link>
          {discountPct > 0 ? (
            <span className="absolute top-2 left-2 w-10 h-10 flex items-center justify-center bg-[#fd9e10] text-white text-xs font-semibold rounded-full">
              {discountPct}%
            </span>
          ) : hasPromo ? (
            <span className="absolute top-2 left-2 bg-[#fd9e10] text-white text-xs font-semibold px-2 py-1 rounded">
              {getPromotionName(product)}
            </span>
          ) : null}

          {/* Hover overlay: icons on right, Add to Cart at bottom of image */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end pointer-events-none">
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 pointer-events-auto">
              <button
                type="button"
                onClick={handleCompare}
                className="w-9 h-9 rounded-full bg-white/95 shadow flex items-center justify-center text-gray-700 hover:bg-[#ff9100] hover:text-white transition-colors"
                aria-label="Compare"
              >
                <ArrowLeftRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handlePreview}
                className="w-9 h-9 rounded-full bg-white/95 shadow flex items-center justify-center text-gray-700 hover:bg-[#ff9100] hover:text-white transition-colors"
                aria-label="Quick preview"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={toggleWishlist}
                className="w-9 h-9 rounded-full bg-white/95 shadow flex items-center justify-center text-gray-700 hover:bg-[#ff9100] hover:text-white transition-colors"
                aria-label="Wishlist"
              >
                <Heart
                  className="h-4 w-4"
                  fill={wishlisted ? 'currentColor' : 'none'}
                  stroke="currentColor"
                />
              </button>
            </div>
            <div className="p-2 mt-auto pointer-events-auto">
              <button
                type="button"
                onClick={handleAddToCart}
                className="w-full py-2.5 bg-[#ff9100] text-white rounded-lg text-sm font-medium hover:bg-[#ff7a00] transition-colors"
              >
                Add To Cart
              </button>
            </div>
          </div>
        </div>

        <div className="p-4">
          {colorVariants.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {colorVariants.map((variant, index) => {
                const isActive = activeVariantIndex === index;
                const swatchColor = variant.color?.trim();
                const swatchStyle = swatchColor && /^(#|rgb|hsl)/i.test(swatchColor)
                  ? { backgroundColor: swatchColor }
                  : { backgroundColor: swatchColor || '#d1d5db' };

                return (
                  <button
                    key={variant.id}
                    type="button"
                    aria-label={`Show ${variant.color} variant`}
                    title={variant.color}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedVariantIndex(index);
                    }}
                    className={`h-5 w-5 rounded-full border-2 transition-all ${
                      isActive ? 'border-[#ff9100] scale-110' : 'border-gray-300 hover:border-[#ff9100]'
                    }`}
                    style={swatchStyle}
                  />
                );
              })}
            </div>
          )}
          <Link to={`/product/${product.id}`}>
            <h3 className="font-semibold text-[var(--foreground)] line-clamp-2 hover:underline">
              {product.name}
            </h3>
          </Link>
          {thumbnailImages.length > 1 && (
            <div className="mt-3 overflow-x-auto scrollbar-hide scroll-smooth">
              <div className="flex items-center justify-start gap-2 min-w-max">
                {thumbnailImages.map((image, index) => (
                  <button
                    key={`${product.id}-${image}-${index}`}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleThumbnailClick(index);
                    }}
                    className={`h-12 w-12 overflow-hidden rounded-lg border-2 bg-white p-0.5 transition-all flex-shrink-0 ${
                      selectedImageIndex === index ? 'border-[#ff9100] shadow-lg' : 'border-gray-200 hover:border-[#ff9100]'
                    }`}
                    aria-label={`Show image ${index + 1}`}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${product.name} view ${index + 1}`}
                      loading="lazy"
                      className=" w-full h-full object-inharit object-center"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.stock != null && (
            <p className="text-xs text-center text-gray-500 mt-1">Stock: {product.stock}</p>
          )}
          <div className="flex items-center justify-center gap-2 mt-2">
            {basePrice > displayPrice && (
              <span className="text-sm text-gray-500 line-through">
                Rs {basePrice.toLocaleString()}
              </span>
            )}
            <span
              className={`text-lg font-bold ${basePrice > displayPrice ? 'text-[#fd9e10]' : 'text-[var(--foreground)]'}`}
            >
              Rs {displayPrice.toLocaleString()}
            </span>
          </div>
          {product.rating != null && (
            <p className="text-sm text-gray-500 mt-1">
              ★ {product.rating}
              {product.reviewCount != null && ` (${product.reviewCount})`}
            </p>
          )}
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Product preview"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end p-2 sticky top-0 bg-white z-10">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-black" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6 p-6">
              <div className="flex flex-col items-center">
                <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
                  <img
                    src={displayedImageUrl}
                    alt={product.name}
                    className="w-full h-full object-fill"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-start">
                <h3 className="font-semibold text-lg text-black">
                  {product.name}
                </h3>
                {product.category_name && (
                  <p className="text-xs text-orange-600 mt-1">Category: {product.category_name}</p>
                )}
                {product.vendor_name && (
                  <p className="text-xs text-gray-700">Vendor: {product.vendor_name}</p>
                )}
                {product.stock != null && (
                  <p className="text-sm text-gray-700 mt-1">Stock: {product.stock}</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xl font-bold text-black">
                    Rs {displayPrice.toLocaleString()}
                  </span>
                  {product.base_price && parseFloat(product.base_price) > displayPrice && (
                    <span className="text-sm text-gray-700 line-through">
                      Rs {parseFloat(product.base_price).toLocaleString()}
                    </span>
                  )}
                </div>
                {product.special_price != null && product.special_price !== '' && (
                  <p className="text-xs text-gray-600">Special price: Rs {product.special_price}</p>
                )}
                {product.description && (
                  <p className="text-sm text-gray-800 mt-2 whitespace-pre-line">
                    {product.description}
                  </p>
                )}
                {(product.rating != null || product.reviewCount != null) && (
                  <p className="text-sm text-gray-700 mt-1">
                    ★ {product.rating ?? '-'}
                    {product.reviewCount != null && ` (${product.reviewCount} reviews)`}
                  </p>
                )}
                {product.specification && Object.keys(product.specification).length > 0 && (
                  <div className="mt-2 text-xs text-gray-800">
                    <span className="font-medium">Specification: </span>
                    {JSON.stringify(product.specification)}
                  </div>
                )}
                {product.variants && product.variants.length > 0 && (
                  <p className="text-xs text-gray-700 mt-1">
                    {product.variants.length} variant(s)
                  </p>
                )}
                {product.extra && Object.keys(product.extra).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-300 space-y-1">
                    <p className="text-xs font-medium text-gray-700 uppercase">More from API</p>
                    {Object.entries(product.extra).map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-800">
                        {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </p>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 mt-4">
                  <Link
                    to={`/product/${product.id}`}
                    className="flex-1 py-2.5 text-center bg-[#ff9100] text-white rounded-lg font-medium hover:bg-[#ff7a00]"
                    onClick={() => setPreviewOpen(false)}
                  >
                    View full details
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => {
                      handleAddToCart(e);
                      setPreviewOpen(false);
                    }}
                    className="flex-1 py-2.5 bg-[#0b0320] text-white rounded-lg font-medium hover:opacity-90"
                  >
                    Add To Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}